import React, { useState, useEffect } from 'react';
import { Search, Plus, Power, Pencil, X, Trash2 } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Dialog, DialogContent } from '../ui/dialog';
import { adminClosersAPI } from '../../utils/api';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../ui/toaster';

const money = (n) => `$${Number(n || 0).toLocaleString('es-MX')}`;

const emptyForm = { name: '', email: '', password: '', code: '', discount_percent: 10 };

const ClosersManagement = () => {
  const [closers, setClosers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [rankingVisible, setRankingVisible] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const loadClosers = async () => {
    try {
      setLoading(true);
      const res = await adminClosersAPI.getAll(searchTerm ? { search: searchTerm } : {});
      if (res?.success) setClosers(res.data || []);
    } catch (err) {
      toast({ title: 'Error', description: 'No se pudieron cargar los closers', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(loadClosers, searchTerm ? 300 : 0);
    return () => clearTimeout(t);
  }, [searchTerm]); // eslint-disable-line

  useEffect(() => {
    adminClosersAPI
      .getSettings()
      .then((res) => {
        if (res?.success) setRankingVisible(!!res.data.closer_ranking_visible);
      })
      .catch(() => {});
  }, []);

  const toggleRanking = async () => {
    const next = !rankingVisible;
    setRankingVisible(next);
    try {
      await adminClosersAPI.updateSettings({ closer_ranking_visible: next });
      toast({
        title: next ? 'Ranking visible' : 'Ranking oculto',
        description: next
          ? 'Los closers ven el ranking en su panel.'
          : 'Los closers ya no ven el ranking.',
      });
    } catch (err) {
      setRankingVisible(!next);
      toast({ title: 'Error', description: 'No se pudo cambiar la configuración', variant: 'destructive' });
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (closer) => {
    setEditing(closer);
    setForm({
      name: closer.name,
      email: closer.email,
      password: '',
      code: closer.code || '',
      discount_percent: closer.discount_percent ?? 10,
    });
    setFormError(null);
    setModalOpen(true);
  };

  const handleSave = async () => {
    setFormError(null);
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        code: form.code.toUpperCase(),
        discount_percent: Number(form.discount_percent),
      };
      // En edición la contraseña es opcional: vacía = no cambiarla.
      if (form.password) payload.password = form.password;

      const res = editing
        ? await adminClosersAPI.update(editing.id, payload)
        : await adminClosersAPI.create({ ...payload, password: form.password });

      if (res?.success) {
        toast({ title: editing ? 'Closer actualizado' : 'Closer creado', description: res.message });
        setModalOpen(false);
        loadClosers();
      }
    } catch (err) {
      const msg = err?.errors
        ? Object.values(err.errors).flat().join(' ')
        : err?.message || 'No se pudo guardar el closer';
      setFormError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (closer) => {
    const aviso =
      `¿Eliminar a ${closer.name}?\n\n` +
      `Su código ${closer.code || '(sin código)'} quedará desactivado y ya no se podrá usar en el checkout. ` +
      `Su historial de activaciones también se elimina. Esto no se puede deshacer.`;
    if (!window.confirm(aviso)) return;
    try {
      const res = await adminClosersAPI.delete(closer.id);
      if (res?.success) {
        toast({ title: 'Closer eliminado', description: res.message });
        loadClosers();
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: err?.message || 'No se pudo eliminar el closer',
        variant: 'destructive',
      });
    }
  };

  const handleToggle = async (closer) => {
    try {
      const res = await adminClosersAPI.toggle(closer.id);
      if (res?.success) {
        toast({ title: res.message });
        loadClosers();
      }
    } catch (err) {
      toast({ title: 'Error', description: 'No se pudo cambiar el estado', variant: 'destructive' });
    }
  };

  return (
    <div className="p-6 space-y-6">
      <Toaster />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Closers</h2>
          <p className="text-gray-500">Vendedores en piso, sus códigos y comisiones del mes</p>
        </div>
        <Button onClick={openCreate} className="bg-black text-white rounded-full h-10 px-5 gap-2 hover:opacity-90">
          <Plus className="w-4 h-4" /> Nuevo closer
        </Button>
      </div>

      {/* Toggle del ranking */}
      <div className="bg-white border border-gray-200 rounded-2xl px-5 py-4 flex items-center justify-between gap-4">
        <div>
          <div className="text-sm font-semibold text-gray-900">Ranking entre closers</div>
          <div className="text-xs text-gray-500 mt-0.5">
            Cuando está activo, cada closer ve la tabla de posiciones del día en su panel.
          </div>
        </div>
        <button
          onClick={toggleRanking}
          className={`relative w-12 h-7 rounded-full transition-colors flex-shrink-0 ${rankingVisible ? 'bg-black' : 'bg-gray-300'}`}
        >
          <span
            className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-all ${rankingVisible ? 'left-6' : 'left-1'}`}
          />
        </button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        <Input
          type="text"
          placeholder="Buscar por nombre, correo o código..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-gray-50 border-gray-300 text-gray-900"
        />
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Cargando...</div>
      ) : closers.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          No hay closers todavía. Crea el primero con “Nuevo closer”.
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Nombre</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Correo</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Código</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Descuento</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Activaciones (mes)</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Comisión (mes)</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Estado</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {closers.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 text-gray-900 font-medium">{c.name}</td>
                    <td className="px-6 py-4 text-gray-600">{c.email}</td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 tracking-wide">
                        {c.code || '—'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{c.discount_percent}%</td>
                    <td className="px-6 py-4 text-gray-900 font-semibold">{c.month_activations}</td>
                    <td className="px-6 py-4 text-gray-900 font-semibold">{money(c.month_commission)}</td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          c.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {c.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEdit(c)}
                          title="Editar"
                          className="p-2 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                        >
                          <Pencil className="w-4 h-4 text-gray-500" />
                        </button>
                        <button
                          onClick={() => handleToggle(c)}
                          title={c.is_active ? 'Desactivar' : 'Activar'}
                          className="p-2 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                        >
                          <Power className={`w-4 h-4 ${c.is_active ? 'text-green-600' : 'text-gray-400'}`} />
                        </button>
                        <button
                          onClick={() => handleDelete(c)}
                          title="Eliminar"
                          className="p-2 rounded-lg border border-gray-200 hover:bg-red-50 hover:border-red-200 transition-colors"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal crear/editar */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-white max-w-md rounded-3xl p-0 overflow-hidden">
          <div className="px-6 pt-6 pb-4 flex items-start justify-between">
            <div>
              <h3 className="text-lg font-bold text-gray-900">
                {editing ? 'Editar closer' : 'Nuevo closer'}
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                El código se crea también en Stripe para aplicar el descuento en el checkout.
              </p>
            </div>
            <button onClick={() => setModalOpen(false)} className="p-1 rounded-lg hover:bg-gray-100">
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>

          <div className="px-6 pb-6 space-y-3">
            <div>
              <label className="text-xs font-medium text-gray-600">Nombre</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ana Pérez"
                className="mt-1 bg-gray-50 border-gray-300"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">Correo (usuario de acceso)</label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="ana@vondi.mx"
                className="mt-1 bg-gray-50 border-gray-300"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">
                Contraseña {editing && <span className="text-gray-400">(vacía = sin cambio)</span>}
              </label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Mínimo 8 caracteres"
                className="mt-1 bg-gray-50 border-gray-300"
              />
            </div>
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="text-xs font-medium text-gray-600">Código</label>
                <Input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
                  placeholder="ANA10"
                  className="mt-1 bg-gray-50 border-gray-300 tracking-wide"
                />
              </div>
              <div className="w-28">
                <label className="text-xs font-medium text-gray-600">Descuento %</label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={form.discount_percent}
                  onChange={(e) => setForm({ ...form, discount_percent: e.target.value })}
                  className="mt-1 bg-gray-50 border-gray-300"
                />
              </div>
            </div>

            {editing && (
              <p className="text-xs text-gray-500 leading-relaxed">
                Cambiar el código o el porcentaje genera un cupón nuevo en Stripe y desactiva el anterior.
                Las ventas ya registradas no se modifican.
              </p>
            )}

            {formError && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                <p className="text-red-600 text-sm">{formError}</p>
              </div>
            )}

            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full bg-black text-white rounded-full h-12 font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {saving ? 'Guardando...' : editing ? 'Guardar cambios' : 'Crear closer'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClosersManagement;
